/**
 * PATH: packages/email-hub/src/feature/email-reader/ReaderNavigationBar.tsx
 * FILE_ID: EMAIL-READER-NAV-BAR-001
 * PURPOSE: Keep Back, Previous, Next, and reader position permanently reachable.
 */

import { ArrowLeft, ChevronLeft, ChevronRight, X } from 'lucide-react'

import { cn } from '../../_internal/lib/utils'
import { resolveReaderPosition } from './navigation'

interface ReaderNavigationBarProps {
  currentId: string
  orderedIds: readonly string[]
  narrow: boolean
  onNavigate: (emailId: string) => void
  onClose: () => void
}

export function ReaderNavigationBar({
  currentId,
  orderedIds,
  narrow,
  onNavigate,
  onClose,
}: ReaderNavigationBarProps) {
  const position = resolveReaderPosition(orderedIds, currentId)

  return (
    <div
      className="flex h-10 shrink-0 items-center gap-1 border-b border-border bg-background/90 px-2 backdrop-blur-sm"
      data-testid="email-reader-navigation"
    >
      <button
        type="button"
        onClick={onClose}
        className="inline-flex h-8 shrink-0 items-center gap-1 rounded-md px-2 text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
        title={narrow ? 'Back to inbox' : 'Close reader'}
        aria-label={narrow ? 'Back to inbox' : 'Close reader'}
      >
        {narrow ? (
          <ArrowLeft className="h-3.5 w-3.5" />
        ) : (
          <X className="h-3.5 w-3.5" />
        )}
        {narrow && <span>Inbox</span>}
      </button>

      <span className="min-w-0 flex-1 truncate text-center text-[10px] tabular-nums text-muted-foreground">
        {position.index >= 0
          ? `${position.index + 1} of ${position.total}`
          : `${position.total} loaded`}
      </span>

      <button
        type="button"
        onClick={() => {
          if (position.previousId) onNavigate(position.previousId)
        }}
        disabled={!position.previousId}
        className={cn(
          'inline-flex h-8 items-center gap-1 rounded-md px-2 text-xs',
          'text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-35',
        )}
        title="Previous email"
        aria-label="Previous email"
      >
        <ChevronLeft className="h-3.5 w-3.5" />
        {!narrow && <span>Previous</span>}
      </button>
      <button
        type="button"
        onClick={() => {
          if (position.nextId) onNavigate(position.nextId)
        }}
        disabled={!position.nextId}
        className={cn(
          'inline-flex h-8 items-center gap-1 rounded-md px-2 text-xs',
          'text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-35',
        )}
        title="Next email"
        aria-label="Next email"
      >
        {!narrow && <span>Next</span>}
        <ChevronRight className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}
